import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../App.jsx';
import api from '../services/api.js';

export default function NavBar({ unreadConns: unreadProp }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [points, setPoints] = useState(user?.total_points ?? 0);
  const [unread, setUnread] = useState(unreadProp ?? 0);
  const [pendingDuels, setPendingDuels] = useState(0);
  const [pendingFriends, setPendingFriends] = useState(0);
  const [showMore, setShowMore] = useState(false);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });
  const moreBtnRef = useRef(null);
  const navRef = useRef(null);
  const measureRef = useRef(null);   // rangée cachée qui mesure la largeur des onglets
  const starsRef = useRef(null);     // le trophée (largeur réservée à droite)
  const [visibleCount, setVisibleCount] = useState(10);
  const [navHeight, setNavHeight] = useState(0);
  const [widths, setWidths] = useState(null);   // { items: [px…], plus: px }

  useEffect(() => { if (unreadProp !== undefined) setUnread(unreadProp); }, [unreadProp]);

  const refreshMe = () => api.getMe().then(u => {
    setPoints(u.total_points || 0);
  }).catch(() => {});

  useEffect(() => {
    refreshMe();
    if (unreadProp === undefined) api.getUnreadConnections().then(r => setUnread(r.unread || 0)).catch(() => {});
    api.getPendingDuels().then(r => setPendingDuels(r.pending || 0)).catch(() => {});
    api.getPendingFriends().then(r => setPendingFriends(r.received || 0)).catch(() => {});
  }, []);

  useEffect(() => {
    refreshMe();
    api.getUnreadConnections().then(r => setUnread(r.unread || 0)).catch(() => {});
    api.getPendingDuels().then(r => setPendingDuels(r.pending || 0)).catch(() => {});
    api.getPendingFriends().then(r => setPendingFriends(r.received || 0)).catch(() => {});
  }, [location.pathname]);

  // Pastilles rafraîchies AUSSI sur pull-to-refresh (le hook émet 'curio:refresh-badges')
  // → un tirage met à jour la page ET les compteurs amis/duels/connexions de la navbar.
  useEffect(() => {
    const refreshBadges = () => {
      refreshMe();
      if (unreadProp === undefined) api.getUnreadConnections().then(r => setUnread(r.unread || 0)).catch(() => {});
      api.getPendingDuels().then(r => setPendingDuels(r.pending || 0)).catch(() => {});
      api.getPendingFriends().then(r => setPendingFriends(r.received || 0)).catch(() => {});
    };
    window.addEventListener('curio:refresh-badges', refreshBadges);
    return () => window.removeEventListener('curio:refresh-badges', refreshBadges);
  }, []);

  const isActive = (path) => location.pathname === path;
  const go = (path) => { setShowMore(false); navigate(path); };

  const mainItems = [
    { path: '/', icon: '🏠', label: 'Accueil' },
    { path: '/curiosaurus', icon: '🌳', label: 'Arbre' },
    { path: '/duels', icon: '⚔️', label: 'Duels', badge: pendingDuels },
    { path: '/friends', icon: '👥', label: 'Amis', badge: pendingFriends },
    { path: '/blitz', icon: '⚡', label: 'Éclair' },
    { path: '/connections', icon: '🔗', label: 'Connexions', badge: unread },
  ];

  const fixedMoreItems = [
    { path: '/cards', icon: '📚', label: 'Fiches' },
    { path: '/ranking', icon: '🏅', label: 'Rang' },
    { path: '/profile', icon: '👤', label: 'Profil' },
    ...(user?.is_admin ? [{ path: '/admin', icon: '🛠️', label: 'Admin' }] : []),
  ];

  const allItems = [...mainItems, ...fixedMoreItems];
  const itemCount = allItems.length;

  // Hauteur de la barre (pour d'éventuels décalages de contenu).
  useEffect(() => {
    const measureH = () => { if (navRef.current) setNavHeight(navRef.current.offsetHeight); };
    measureH();
    window.addEventListener('resize', measureH);
    const t = setTimeout(measureH, 300);
    return () => { window.removeEventListener('resize', measureH); clearTimeout(t); };
  }, []);

  // 1) Mesure la largeur INTRINSÈQUE de chaque onglet + du "Plus", une seule fois
  //    (re-mesuré uniquement si un badge apparaît/disparaît → sa largeur change).
  //    Rangée cachée hors flux → mesure STABLE, jamais faussée par une transition.
  useLayoutEffect(() => {
    const m = measureRef.current;
    if (!m) return;
    const kids = Array.from(m.children);
    if (kids.length < itemCount + 1) return;
    const items = kids.slice(0, itemCount).map(c => c.getBoundingClientRect().width);
    const plus = kids[itemCount].getBoundingClientRect().width;
    setWidths({ items, plus });
  }, [itemCount, pendingDuels, pendingFriends, unread]);

  // 2) Combien d'onglets tiennent = fonction de la LARGEUR de la barre (déduite
  //    de la largeur d'écran) + des largeurs mesurées. On réserve TOUJOURS la
  //    place du "Plus" dès qu'il faut en cacher au moins un → plus d'oscillation.
  //    Recalcul seulement quand la largeur change (resize / rotation), pas au clic.
  useLayoutEffect(() => {
    if (!widths) return;
    const compute = () => {
      const nav = navRef.current;
      if (!nav) return;
      const cs = getComputedStyle(nav);
      const padX = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
      const trophyW = starsRef.current ? starsRef.current.getBoundingClientRect().width : 0;
      const gap = 2;
      const avail = nav.clientWidth - padX - trophyW - gap;
      const totalAll = widths.items.reduce((a, w) => a + w + gap, 0);
      if (totalAll <= avail) { setVisibleCount(itemCount); return; }
      const budget = avail - widths.plus - gap;   // réserver la place du "Plus"
      let used = 0, count = 0;
      for (const w of widths.items) {
        if (used + w + gap > budget) break;
        used += w + gap; count++;
      }
      setVisibleCount(Math.max(1, count));
    };
    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('orientationchange', compute);
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('orientationchange', compute);
    };
  }, [widths, itemCount]);

  const visibleItems = allItems.slice(0, visibleCount);
  const overflowItems = allItems.slice(visibleCount);
  const hasOverflow = overflowItems.length > 0;
  const moreActive = overflowItems.some(i => isActive(i.path));

  const togglePlus = () => {
    if (!showMore && moreBtnRef.current) {
      const rect = moreBtnRef.current.getBoundingClientRect();
      const winWidth = window.innerWidth;
      setMenuPos({
        top: rect.bottom + 6,
        right: Math.max(8, winWidth - rect.right),
      });
    }
    setShowMore(!showMore);
  };

  return (
    <>
      <nav className="main-nav" ref={navRef}>
        <div className="nav-items">
          {visibleItems.map(item => (
            <button key={item.path}
              className={`nav-btn ${isActive(item.path) ? 'active' : ''}`}
              onClick={() => go(item.path)}>
              <span className={`nav-icon ${item.badge ? 'nav-icon-wrap' : ''}`}>
                {item.icon}
                {item.badge > 0 && <span className="nav-badge">{item.badge}</span>}
              </span>
              <span className="nav-label">{item.label}</span>
            </button>
          ))}
        </div>
        {hasOverflow && (
          <button ref={moreBtnRef}
            className={`nav-btn nav-btn-fixed ${moreActive || showMore ? 'active' : ''}`}
            onClick={togglePlus}>
            <span className="nav-icon nav-icon-wrap">
              ☰
              {overflowItems.some(i => i.badge > 0) && (
                <span className="nav-badge nav-badge-dot" />
              )}
            </span>
            <span className="nav-label">Plus</span>
          </button>
        )}
        <div className="nav-stars" ref={starsRef} onClick={() => go('/achievements')} role="button" tabIndex={0}
             title="Tes points" style={{ cursor: 'pointer' }}>
          <span className="nav-points">🏆 {points.toLocaleString('fr-FR')}</span>
        </div>

        {/* Rangée CACHÉE (hors flux) : sert UNIQUEMENT à mesurer la largeur
            intrinsèque de chaque onglet + du "Plus". Pas de classe .nav-items
            (donc pas de flex-grow qui fausserait la mesure). */}
        <div aria-hidden="true" style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
          <div ref={measureRef}
               style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none', display: 'flex', gap: '2px' }}>
          {allItems.map(item => (
            <span key={'m' + item.path} className="nav-btn">
              <span className={`nav-icon ${item.badge ? 'nav-icon-wrap' : ''}`}>
                {item.icon}
                {item.badge > 0 && <span className="nav-badge">{item.badge}</span>}
              </span>
              <span className="nav-label">{item.label}</span>
            </span>
          ))}
          <span className="nav-btn nav-btn-fixed">
            <span className="nav-icon nav-icon-wrap">☰</span>
            <span className="nav-label">Plus</span>
          </span>
          </div>
        </div>
      </nav>

      {showMore && hasOverflow && (
        <>
          <div className="more-overlay" onClick={() => setShowMore(false)} />
          <div className="more-menu" style={{ top: menuPos.top, right: menuPos.right }}>
            {overflowItems.map(item => (
              <button key={item.path}
                className={`more-item ${isActive(item.path) ? 'active' : ''}`}
                onClick={() => go(item.path)}>
                <span className="more-icon">{item.icon}</span>
                <span>{item.label}</span>
                {item.badge > 0 && <span className="nav-badge" style={{ position: 'static', marginLeft: 'auto' }}>{item.badge}</span>}
              </button>
            ))}
          </div>
        </>
      )}
    </>
  );
}
